import { useContext } from 'react'
import { useRouter } from 'next/router'
import { useQueryNext } from '../../../componentsF/hooksF/useQueryNextF.js'
import { GameContext } from '../GameContextF.jsx'
import { Box, VStack } from '@chakra-ui/react'
import { Ptext } from '../../../componentsF/Chakra3F/ChakraF.jsx'
import { old_game_pathname, oldgames_page } from './urlsF.mts'
import { TileLinks } from '../../../componentsF/TileLinksF.jsx'
import { ErrLoadingWrap } from '../../../componentsF/ErrLoadingF.jsx'
import { gql } from '@apollo/client'
import { useNow } from '../../../componentsF/hooksF/useNowF.jsx'
import * as _ from 'lodash-es'

import { mkMsToLuxH } from '../../../sharedF/timeF/mkMsToLuxHF.mts'

const GetRecentGamesQuery = gql`query ($gameRoom: String!, $nonce: String) {
  getRecentGames(gameRoom: $gameRoom, nonce: $nonce) {
    id
    created_at, updated_at
    playerNamesA
  }
}`

const useGetRecentGames = (/** @type {{gameRoom?: string | string[]}} */ {gameRoom: gameRoomProp} = {}) => {
  const gameCtx = useContext(GameContext)
  const gameRoom = gameRoomProp || gameCtx?.gameRoom
  const nonce = gameCtx?.id

  const {data, loading, error} = useQueryNext(GetRecentGamesQuery, {
    variables: {gameRoom, nonce},
    skip: !gameRoom,
  })

  let {getRecentGames} = data || {}
  getRecentGames ||= []
  getRecentGames = _.map(getRecentGames, mkMsToLuxH(['created_at', 'updated_at']))

  return {getRecentGames, loading, error, gameRoom}
}

export const useGameRecentlyFinished = () => {
  const gameCtx = useContext(GameContext)
  const {myState: {publicState: {myTaskName}}} = gameCtx
  const {now} = useNow()

  const {getRecentGames, loading, error} = useGetRecentGames()

  const justFinishedGameId = gameCtx.justFinishedGameId
  const mostRecent = getRecentGames && getRecentGames[0]
  const isInitialDescription = !myTaskName || myTaskName == 'InitialPrompt'

  if (justFinishedGameId && isInitialDescription) {
    return {isRecentlyFinished: true, mostRecent: {id: justFinishedGameId}}
  }

  if (mostRecent && isInitialDescription) {
    const {created_at} = mostRecent
    if (!created_at) return {isRecentlyFinished: false, mostRecent}
    const elapsedMin = -created_at.diff(now).as('minutes')
    if (elapsedMin < 5) {
      return {isRecentlyFinished: true, mostRecent}
    }
  }
  return {isRecentlyFinished: false, mostRecent}
}

export const RecentGames = ({children}) => {
  const {gameRoom} = useContext(GameContext)

  /** @type {Array<{pathname: string, query: Record<string, any>, name: string, bg: string, color: string}>} */
  let tileItems = [
    {
      pathname: oldgames_page, query: {gameRoom},
      name: 'View Past Games',
      bg: 'lightgreen', color: 'black',
    }
  ]

  const {isRecentlyFinished, mostRecent} = useGameRecentlyFinished()
  if (isRecentlyFinished) {
    tileItems.push({
      name: 'See Results!',
      bg: 'blue', color: 'white',
      pathname: old_game_pathname,
      query: {
        ppOldGameId: mostRecent.id,
        gameRoom,
      },
    })
  }

  return <>
    <TileLinks {...{tileItems, mb: 3, columns: 1}} />
    {children}
  </>

}

const gamesListGridCols = {base: '1fr auto', md: '15rem 1fr auto'}
const gamesListOddStripe = {bg: 'gray.100', _dark: {bg: 'gray.700'}}

const GamesListRow = ({game, gameRoom, isOdd}) => {
  const router = useRouter()
  const {id, created_at, updated_at, playerNamesA, gameRoom: rowGameRoom} = game
  const room = rowGameRoom || gameRoom
  const dt = created_at?.toFormat ? created_at : (updated_at?.toFormat ? updated_at : null)
  const playerCount = playerNamesA?.length || 0
  const gameUrl = `/oldgames/${encodeURIComponent(room)}/${encodeURIComponent(id)}`
  const onClick = (e) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1) return
    e.preventDefault()
    router.push(gameUrl)
  }
  return <Box {...{as: 'a', href: gameUrl, onClick,
    cursor: 'pointer', _hover: {opacity: 0.7}, p: 2, fontSize: 'xl', textAlign: 'left',
    display: 'grid', gridTemplateColumns: gamesListGridCols, alignItems: 'center', gap: 3,
    textDecoration: 'none', color: 'inherit',
    ...(isOdd ? gamesListOddStripe : {})}}>
    <Box {...{whiteSpace: 'nowrap'}}>{dt?.toFormat('ccc ff') || room}</Box>
    <Box {...{display: {base: 'none', md: 'block'}, textAlign: 'right'}}>{playerNamesA?.join(', ')}</Box>
    <Box {...{textAlign: 'right'}}>{playerCount > 0 ? `${playerCount}p` : ''}</Box>
  </Box>
}

const GamesListTable = (/** @type {{games?: Array<{id: string, created_at?: any, updated_at?: any, playerNamesA?: string[], gameRoom?: string}>, gameRoom?: string | string[]}} */ {games, gameRoom}) => {
  if (!games?.length) return null
  return <VStack {...{align: 'stretch', spacing: 0}}>
    {games.map((game, i) => <GamesListRow key={game.id} {...{game, gameRoom, isOdd: i % 2 === 1}} />)}
  </VStack>
}

export const RecentGamesList = (/** @type {{games?: Array<Record<string, any>>, gameRoom?: string | string[], limit?: number, title?: string, [key: string]: any}} */ {games: gamesProp, gameRoom: gameRoomProp, limit, title, ...boxProps}) => {
  const {getRecentGames, loading, error, gameRoom} = useGetRecentGames({gameRoom: gameRoomProp})
  const sliceGames = (games) => limit ? games.slice(0, limit) : games

  if (gamesProp) {
    const gamesWithLux = _.map(gamesProp, mkMsToLuxH(['created_at', 'updated_at']))
    return <Box {...boxProps}>
      {title && <Ptext {...{fontSize: 'md', color: 'gray.400', mb: 1}}>{title}</Ptext>}
      <GamesListTable {...{games: sliceGames(gamesWithLux)}} />
    </Box>
  }

  return <Box {...boxProps}>
    <ErrLoadingWrap {...{error, loading}}>
      {title && <Ptext {...{fontSize: 'md', color: 'gray.400', mb: 1}}>{title}</Ptext>}
      <GamesListTable {...{games: sliceGames(getRecentGames), gameRoom}} />
    </ErrLoadingWrap>
  </Box>
}
